<template>
	<!-- LSP-20787 (D18, §6 S2/S3): the prototype's inline repair box — no dialog, no leaving the page. The parent
	     (EntityHeader's row, the page's mobile block, SmsDeliveryBanner) decides whether it is open and in which mode,
	     and mounts it fresh each time. -->
	<div class="sms-delivery-repair d-flex flex-column ga-1" data-test-id="sms-delivery-repair-panel">
		<span class="sms-delivery-repair__label">{{ mode === "phone" ? "Mobile number" : "Email address" }}</span>
		<!-- LSP-20787 item 10: the buttons sit on the input's own row, vertically centred on the input control.
		     align-end previously pinned them to the bottom of the field column (the counter/error line under the
		     field), not to the input itself. -->
		<div class="sms-delivery-repair__row d-flex flex-wrap align-center ga-3">
			<div class="sms-delivery-repair__field d-flex flex-column ga-1">
				<div
					v-if="mode === 'phone'"
					ref="phoneField"
					:style="{
						'--country-field-top': `${phoneTop}px`,
						'--country-field-bottom': `${phoneBottom}px`,
						'--country-field-left': `${phoneLeft}px`,
						'--country-field-width': `${phoneWidth}px`
					}"
					@paste.prevent="pastePhone">
					<vue-tel-input
						v-bind="vueTelProps"
						v-model="phoneValue"
						:input-options="telInputOptions"
						:class="{ 'error--text': !!phoneError }"
						style="border-radius: 8px; padding: 8px"
						valid-color="#000000"
						data-test-id="sms-delivery-repair-phone"
						warn-on-typed-country-change
						@open="openCountryList" />
				</div>
				<EmailTextField
					v-else
					v-model="emailValue"
					ref="emailFieldRef"
					:rules="[emailRequiredRule]"
					:max-length="FIELD_LENGTHS.EMAIL"
					aria-label="Email address"
					autofocus
					data-test-id="sms-delivery-repair-email" />
				<span v-if="mode === 'phone' && phoneError" class="text-caption text-error" role="alert">{{ phoneError }}</span>
			</div>
			<v-btn
				data-test-id="sms-delivery-repair-submit"
				rounded="pill"
				color="primary"
				variant="flat"
				:loading="saving"
				:disabled="saving"
				@click="submit">
				{{ mode === "phone" ? "Save & resend" : "Send email" }}
			</v-btn>
			<v-btn
				data-test-id="sms-delivery-repair-cancel"
				rounded="xl"
				color="grey"
				variant="text"
				:disabled="saving"
				@click="emit('close')">
				Cancel
			</v-btn>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, nextTick, ref, watch } from "vue";
	import { useElementBounding } from "@vueuse/core";
	import { useToast } from "vue-toastification";
	import { parsePhoneNumberFromString } from "libphonenumber-js";
	import api from "@/api";
	import type { SmsDeliveryRepairCustomer, SmsDeliveryRepairParticipant } from "@/helpers/smsDelivery";
	import { notifyInvitationDelivery } from "@/helpers/invitationSkipReasons";
	import { getVueTelInputOptions, getVueTelProps, isPhoneNumberValid, toE164 } from "@/helpers/phone";
	import { getMessageFromError, getTrimmedPhoneFromClipboard } from "@/helpers/utilities";
	import { FIELD_LENGTHS } from "@/helpers/Validation";

	const props = defineProps<{
		conversationId: string;
		mode: "phone" | "email";
		customer: SmsDeliveryRepairCustomer | null;
		participants?: SmsDeliveryRepairParticipant[] | null;
	}>();
	const emit = defineEmits<{
		// Bugbot: conversationId is the id the send was actually made for, so a caller several hops up
		// (EntityDetailPage) can tell a send that finishes after an in-place session switch apart from a current one.
		(event: "resent", conversationId: string): void;
		(event: "customerUpdated", customer: SmsDeliveryRepairCustomer): void;
		(event: "refresh"): void;
		(event: "close"): void;
	}>();
	const toast = useToast();
	const baseVueTelProps = getVueTelProps();
	const vueTelProps = {
		...baseVueTelProps,
		dropdownOptions: { ...(baseVueTelProps.dropdownOptions as Record<string, unknown>), ariaLabel: "Country" }
	};
	const phoneField = ref<HTMLElement>();
	const {
		top: phoneTop,
		bottom: phoneBottom,
		left: phoneLeft,
		width: phoneWidth,
		update: updatePhoneBounds
	} = useElementBounding(phoneField);

	async function openCountryList() {
		await nextTick();
		updatePhoneBounds();
		const list = phoneField.value?.querySelector<HTMLElement>(".vti__dropdown-list");
		if (!list) return;
		// Keep the library's list, search and selection behavior. The native top layer escapes
		// clipping headers/cards without changing their overflow or the repair panel's height.
		list.popover = "manual";
		list.showPopover();
	}
	// S2: focus in the field, explicitly rather than inherited from the app-wide inputOptions (main.ts:119), so the spec pins it.
	const telInputOptions = getVueTelInputOptions({ placeholder: "Mobile number", autofocus: true });

	// Pre-fill and edit the row the resend will go to. No include orders the rows this box receives
	// (conversations.ts:1692-1701, customers.ts:255-258), so they are put oldest first here.
	const oldestFirst = <Row extends { createdAt?: string }>(rows: Row[] | undefined) =>
		[...(rows ?? [])].sort((a, b) => String(a.createdAt ?? "").localeCompare(String(b.createdAt ?? "")));
	const phones = computed(() => oldestFirst(props.customer?.PhoneNumbers));
	const emails = computed(() => oldestFirst(props.customer?.EmailAddresses));
	// LSP-20787 (22016): this session's participant for the contact — the newest, when there is more than one.
	const sessionParticipant = computed(() => {
		const customerId = props.customer?.id;
		const ownParticipants = (props.participants ?? []).filter((participant) => !!customerId && participant.customerId === customerId);
		return ownParticipants.sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? "")))[0] ?? null;
	});
	// LSP-20787 (22016): the API's rule for which row a resend uses (controllers/conversations.ts
	// syncParticipantContactFromCustomer): the row the session's participant already has; else the one row updated more
	// recently than every other (a repair edits one row in place, so only its updatedAt moves — this also covers a second
	// repair on a page whose Participants predate the first); else the oldest. `rows` is oldest first.
	function rowTheResendUses<Row extends { createdAt?: string; updatedAt?: string }>(
		rows: Row[],
		matchesParticipant: (row: Row) => boolean
	): Row | null {
		const participantRow = rows.find(matchesParticipant);
		if (participantRow) return participantRow;
		// Only a row edited after it was created counts, as the API's sync does (an untouched row's updatedAt is its creation).
		const edited = rows.filter((row) => !!row.updatedAt && !!row.createdAt && new Date(row.updatedAt) > new Date(row.createdAt));
		const [newest, runnerUp] = [...edited].sort((a, b) => String(b.updatedAt ?? "").localeCompare(String(a.updatedAt ?? "")));
		if (newest && (!runnerUp || String(newest.updatedAt ?? "") > String(runnerUp.updatedAt ?? ""))) return newest;
		return rows[0] ?? null;
	}
	// Matched in E.164, the comparison the submit uses below.
	const currentPhone = computed(() => {
		const participantPhone = toE164(sessionParticipant.value?.phone);
		return rowTheResendUses(phones.value, (phone) => !!participantPhone && toE164(phone.phoneNumber) === participantPhone);
	});
	// Email stays on the OLDEST row, as the API's email sync does: PUT /customers bumps every email row's updatedAt, so
	// "most recently updated" cannot name the edited address.
	const currentEmail = computed(() => emails.value[0] ?? null);
	// S2 / S3 / S6: seeded from the contact on every open — the parent mounts the box fresh each time, so a
	// cancelled edit never survives it.
	const phoneValue = ref(currentPhone.value?.phoneNumber ?? "");
	const emailValue = ref(currentEmail.value?.emailAddress ?? "");
	const phoneError = ref("");
	// m2: an edit retracts a shown error at once — the rep should not have to hit Save & resend again
	// just to find out the new value they already typed is fine.
	watch(phoneValue, () => {
		phoneError.value = "";
	});
	const saving = ref(false);
	const emailFieldRef = ref<{ validate: () => Promise<string[]> } | null>(null);
	const emailRequiredRule = (value: unknown) => String(value ?? "").trim().length > 0 || "Enter an email address";

	// PUT /customers/:id replaces the whole collection (controllers/customers.ts:914-918, :996-998): always send
	// every row and edit the pre-filled row in place by id — a lone row would delete its siblings, and an appended
	// number would not be the one the resend uses.
	function phonesWithCurrentReplaced(phoneNumber: string) {
		if (phones.value.length === 0) return [{ phoneNumber }];
		return phones.value.map((phone) => (phone === currentPhone.value ? { ...phone, phoneNumber } : { ...phone }));
	}
	function emailsWithFirstReplaced(emailAddress: string) {
		if (emails.value.length === 0) return [{ emailAddress }];
		return emails.value.map((email, index) => (index === 0 ? { ...email, emailAddress } : { ...email }));
	}

	// The contact page's paste handler (CustomerPage.vue:608-611): digits only, trimmed to the input's max length.
	function pastePhone(event: ClipboardEvent) {
		phoneValue.value = getTrimmedPhoneFromClipboard(event, Number(telInputOptions.maxlength));
	}

	// Always enabled; no-op while invalid (CLAUDE.md "Form action buttons — always enabled, no-op when invalid"):
	// an invalid value shows its error and the click does nothing; the button only disables mid-send.
	async function submit() {
		if (saving.value) return;
		if (props.mode === "phone") {
			// Same validator as the contact page, which mirrors the API's own check (helpers/phone.ts); the PUT carries E.164.
			const phoneNumber = isPhoneNumberValid(phoneValue.value) ? toE164(phoneValue.value) : null;
			if (!phoneNumber) {
				phoneError.value = "Enter a valid phone number";
				return;
			}
			phoneError.value = "";
			// The API stores formatInternational() ("+1 212 567 8901", contact-api db/models/phoneNumber.ts:37-43): compare E.164
			// on both sides, or an untouched number PUTs, advances updatedAt and can turn a plain resend into a 409 (S8).
			// LSP-20787 (22021): E.164 drops an extension, so a stored "+1 984 464 4545 ext. 2" is never "unchanged" — PUT it.
			const storedPhone = currentPhone.value?.phoneNumber ?? "";
			if (phoneNumber === toE164(storedPhone) && !parsePhoneNumberFromString(storedPhone, "US")?.ext) {
				await send("sms", null);
				return;
			}
			// Bugbot: the collection-replace PUT forwards every existing row's id; a row with a missing/null id would
			// be sent as a falsy association id, which the API treats as malformed (or, for a lone id-less row,
			// updates only the first stored row and destroys the rest). Rows from the conversation GET always carry
			// ids today, but refuse the destructive PUT rather than trust that — the empty-collection create path
			// (phones.value.length === 0) is unaffected, since .some() on [] is false.
			if (phones.value.some((phone) => !phone.id)) {
				console.warn("SmsDeliveryRepairPanel: an existing phone row has no id, refusing the destructive PUT", {
					conversationId: props.conversationId,
					channel: "sms"
				});
				toast.error("This contact changed. Refresh the page and try again.");
				return;
			}
			await send("sms", { PhoneNumbers: phonesWithCurrentReplaced(phoneNumber) }, phoneNumber);
			return;
		}
		if (((await emailFieldRef.value?.validate()) ?? []).length > 0) return;
		const emailAddress = emailValue.value.trim();
		if (emailAddress === (currentEmail.value?.emailAddress ?? "")) {
			await send("email", null);
			return;
		}
		if (emails.value.some((email) => !email.id)) {
			console.warn("SmsDeliveryRepairPanel: an existing email row has no id, refusing the destructive PUT", {
				conversationId: props.conversationId,
				channel: "email"
			});
			toast.error("This contact changed. Refresh the page and try again.");
			return;
		}
		await send("email", { EmailAddresses: emailsWithFirstReplaced(emailAddress) });
	}

	// LSP-22013: the one OTHER contact holding exactly this number (E.164 on both sides — the search is an ilike, so it
	// also returns longer numbers containing these digits), or null when there is none, several, or the lookup fails.
	async function otherContactWithNumber(phoneNumber: string, customerId: string) {
		const result = await api.searchCustomers(phoneNumber).catch(() => null);
		const owners = ((result?.rows ?? []) as Array<SmsDeliveryRepairCustomer & { name?: string | null }>).filter(
			(row) => row.id !== customerId && (row.PhoneNumbers ?? []).some((phone) => toE164(phone.phoneNumber) === phoneNumber)
		);
		return owners.length === 1 ? owners[0] : null;
	}

	// `saving` spans the whole write-then-resend, and the conversation id is captured before the first await, so a
	// double click or an entity switch mid-flight cannot send twice or send for another session.
	async function send(channel: "sms" | "email", changes: Record<string, unknown> | null, typedPhone: string | null = null) {
		if (saving.value) return;
		const conversationId = props.conversationId;
		const customer = props.customer;
		// I1: a session whose Customer row is gone (paranoid soft-delete) has nothing to write the rep's edit to.
		// Falling through would silently drop the edit, resend to whatever the conversation already had on file, and
		// still report success — the rep would believe the number/address was corrected when it was not touched at all.
		if (changes && !customer) {
			console.warn("SmsDeliveryRepairPanel: no saved contact to write the rep's edit to", { conversationId, channel });
			toast.error("This session has no saved contact to update");
			return;
		}
		saving.value = true;
		// m1: only true once the write has actually resolved — a PUT that throws (e.g. S8's 409) must not be
		// reported as "wrote the contact" just because one was attempted.
		let wroteContact = false;
		let owner: Awaited<ReturnType<typeof otherContactWithNumber>> = null;
		try {
			if (changes && customer) {
				try {
					// expectedUpdatedAt: a contact edited elsewhere since this page loaded is a 409 CONTACT_CHANGED, never an overwrite.
					const updatedCustomer = await api.updateCustomer(customer.id, { expectedUpdatedAt: customer.updatedAt, ...changes });
					wroteContact = true;
					// S2: before the resend, so a failed resend still leaves the page's contact (and the next expectedUpdatedAt) current.
					emit("customerUpdated", updatedCustomer);
				} catch (error) {
					// LSP-22013: the duplicate-number 400 carries no code, so it is told apart by asking who has the number.
					const httpStatus = (error as { httpStatus?: number } | null)?.httpStatus;
					owner = typedPhone && httpStatus === 400 ? await otherContactWithNumber(typedPhone, customer.id) : null;
					if (!owner) throw error;
					console.warn("SmsDeliveryRepairPanel: the number belongs to another contact, moving the session to it", {
						conversationId,
						ownerCustomerId: owner.id
					});
					// The About tab's Contact change (EntityAbout.vue save): the API swaps the session's contact participant for
					// one with this phone, and the resend texts the owner's row that matches it. customerId is sent, as the About
					// tab sends it, so the API keeps the original contact there instead of treating this as a first-time assign.
					await api.updateConversation(conversationId, {
						customerId: customer.id,
						workflowCustomerId: owner.id,
						newSelectedCustomer: { id: owner.id, phone: typedPhone, email: owner.EmailAddresses?.[0]?.emailAddress ?? null }
					});
				}
			}
			// D16: "sms" for the number repair, "email" for the email repair.
			const response = await api.sendInvitation(conversationId, channel);
			notifyInvitationDelivery(
				toast,
				response.invitations,
				owner
					? `This number belongs to ${owner.name || "another contact"}. The session now uses that contact, and the text has been re-sent.`
					: channel === "email"
						? "The invitation has been sent by email."
						: "The text has been re-sent to the contact.",
				channel
			);
			emit("resent", conversationId); // the parent closes the box
			if (owner) emit("refresh"); // the page reloads the session so its Contact shows the owner
		} catch (error) {
			// M2: identifiers only — contact-admin forwards console.warn to Datadog, and the contact PUT's duplicate-contact
			// 400 message quotes the number or address the rep typed. errorName: httpStatus/errorCode are both undefined
			// for a non-HTTP failure (network TypeError, code bug); the error's own name still identifies it then.
			const { httpStatus, errorCode } = (error ?? {}) as { httpStatus?: number; errorCode?: string };
			const errorName = (error as { name?: string } | null)?.name;
			console.warn("SmsDeliveryRepairPanel: delivery repair failed", {
				conversationId,
				channel,
				wroteContact,
				httpStatus,
				errorCode,
				errorName
			});
			toast.error(getMessageFromError(error, "The invitation could not be sent. Please try again."));
			// S8 / B5: the client rejects with the flat body; contactChangedError() serialises errorCode.
			// LSP-22013: a session that may have been moved to another contact reloads too, even when the text failed.
			if (errorCode === "CONTACT_CHANGED" || owner) emit("refresh");
		} finally {
			saving.value = false;
		}
	}
</script>

<style scoped lang="scss">
	/* The prototype's inline box: accent border, faint blue fill, 16px radius (D18). */
	.sms-delivery-repair {
		border: 1px solid rgb(var(--v-theme-accent));
		background-color: #f7faff;
		border-radius: 16px;
		padding: 16px;
		/* LSP-20787 item 10: hug the field's content instead of stretching across the row; still capped so a
		   narrow container (phone width) shrinks it rather than overflowing. */
		width: fit-content;
		max-width: 100%;
	}
	.sms-delivery-repair :deep(.vti__dropdown-list) {
		position: fixed;
		top: var(--country-field-bottom);
		bottom: auto;
		left: var(--country-field-left);
		right: auto;
		width: var(--country-field-width);
		max-height: 12.5rem;
		margin: 0;
	}
	.sms-delivery-repair :deep(.vti__dropdown-list.above) {
		top: auto;
		bottom: calc(100vh - var(--country-field-top));
	}
	.sms-delivery-repair__field {
		/* LSP-20787 item 10: was 240px — too narrow to show a real address (e.g. "ankitpatel63524@gmail.com")
		   without clipping. max-width keeps it shrinking to the container on narrow screens (flex-wrap, no
		   @media query). */
		width: 320px;
		max-width: 100%;
	}
	.sms-delivery-repair__label {
		font-size: 12px;
		font-weight: 600;
		color: rgba(0, 0, 0, 0.6);
	}
</style>
